import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Status, cn, type StatusTone } from '@lawbee/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { PARTY_ROLES } from '../../../components/IntakeRenderer';
import { api } from '../../../lib/api';
import type { IntakeStatus, MatterType, TeamResponse } from '../../../lib/types';

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const INTAKE_STATUS_LABELS: Record<IntakeStatus, string> = {
  new: 'New',
  needs_info: 'Needs info',
  in_review: 'In review',
  converted: 'Converted',
  declined: 'Declined',
};

const INTAKE_STATUS_VARIANT: Record<IntakeStatus, 'primary' | 'warning' | 'info' | 'success' | 'default'> = {
  new: 'primary',
  needs_info: 'warning',
  in_review: 'info',
  converted: 'success',
  declined: 'default',
};

export function IntakeStatusBadge({ status }: { status: IntakeStatus }) {
  return <Badge variant={INTAKE_STATUS_VARIANT[status] ?? 'default'}>{INTAKE_STATUS_LABELS[status] ?? status}</Badge>;
}

export function ConflictDecisionStatus({ decision }: { decision: 'cleared' | 'potential_conflict' | null }) {
  if (decision === 'cleared') return <Status tone="done">Cleared</Status>;
  if (decision === 'potential_conflict') return <Status tone="blocked">Potential conflict</Status>;
  return (
    <Status tone="idle" className="text-muted-foreground">
      Not checked
    </Status>
  );
}

/** Roles a matter party may have (the API accepts exactly these). */
export const MATTER_PARTY_ROLES: { value: string; label: string }[] = [
  { value: 'opposing_party', label: 'Other side (opposing party)' },
  { value: 'opposing_counsel', label: 'Opposing counsel' },
  { value: 'related_party', label: 'Related person or company' },
  { value: 'witness', label: 'Witness' },
  { value: 'client', label: 'Client' },
  { value: 'spouse', label: 'Spouse or partner' },
  { value: 'other', label: 'Other' },
];

export function roleLabel(role: string | null | undefined): string {
  if (!role) return '';
  const found = PARTY_ROLES.find((r) => r.value === role) ?? MATTER_PARTY_ROLES.find((r) => r.value === role);
  if (found) return found.label;
  if (role === 'client' || role === 'prospective_client') return 'Prospective client';
  return humanizeKey(role);
}

export const DEADLINE_KINDS: { value: string; label: string }[] = [
  { value: 'court', label: 'Court date' },
  { value: 'filing', label: 'Filing' },
  { value: 'limitation', label: 'Limitation period' },
  { value: 'client', label: 'Client' },
  { value: 'spouse', label: 'Spouse or partner' },
  { value: 'internal', label: 'Internal' },
];

export function deadlineKindLabel(kind: string): string {
  return DEADLINE_KINDS.find((k) => k.value === kind)?.label ?? humanizeKey(kind);
}

export function humanizeKey(value: string): string {
  const s = value.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function fullName(first: string | null | undefined, last: string | null | undefined): string {
  return [first, last].filter(Boolean).join(' ').trim() || 'Unnamed';
}

/** Render a stored intake answer for reading. */
export function formatAnswer(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return value.trim() === '' ? '—' : value;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return value
      .map((item) => {
        if (item && typeof item === 'object' && 'name' in item) {
          const p = item as { name?: unknown; role?: unknown };
          const name = typeof p.name === 'string' ? p.name : '';
          const role = typeof p.role === 'string' ? roleLabel(p.role) : '';
          return role ? `${name} (${role})` : name;
        }
        return String(item);
      })
      .filter(Boolean)
      .join(', ');
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function updateStatusTone(status: string): StatusTone {
  if (status === 'sent') return 'done';
  if (status === 'pending_approval') return 'progress';
  if (status === 'failed') return 'blocked';
  return 'idle';
}

export const UPDATE_STATUS_LABELS: Record<string, string> = {
  pending_approval: 'Waiting for approval',
  sent: 'Sent',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

// ---------------------------------------------------------------------------
// Shared queries
// ---------------------------------------------------------------------------

export function useTeam() {
  return useQuery({ queryKey: ['team'], queryFn: () => api.get<TeamResponse>('/api/workspace/team'), staleTime: 60_000 });
}

export function useMatterTypes() {
  return useQuery({ queryKey: ['matter-types'], queryFn: () => api.get<{ types: MatterType[] }>('/api/matters/types'), staleTime: 60_000 });
}

export function matterKey(id: string) {
  return ['matter', id] as const;
}

/** Invalidate the matter detail plus lists that summarize it. */
export function useMatterInvalidate(id: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: matterKey(id) }),
      qc.invalidateQueries({ queryKey: ['matters'] }),
      qc.invalidateQueries({ queryKey: ['dashboard'] }),
    ]);
}

export function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// ---------------------------------------------------------------------------
// Layout bits
// ---------------------------------------------------------------------------

export function Panel({
  title,
  description,
  actions,
  children,
  className,
  contentClassName,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div className="min-w-0 space-y-1">
          <CardTitle>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
      </CardHeader>
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  );
}

export function DefinitionList({ items, className }: { items: { label: string; value: ReactNode }[]; className?: string }) {
  return (
    <dl className={cn('grid gap-x-6 gap-y-3 sm:grid-cols-[minmax(0,12rem)_1fr]', className)}>
      {items.map((it, i) => (
        <div key={`${it.label}-${i}`} className="contents">
          <dt className="text-[13px] text-muted-foreground">{it.label}</dt>
          <dd className="min-w-0 break-words text-sm">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function slugKey(label: string): string {
  const base = label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[^a-z]+/, '')
    .slice(0, 40);
  return base || 'field';
}
